import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Link,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material'
import { Description, ExpandLess, ExpandMore, InfoOutlined, OpenInNew } from '@mui/icons-material'
import { useState } from 'react'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { useGetInteractiveSchemaReportById } from 'src/api/useInteractiveSchema'
import { DivingCertificationEntry, InteractiveSchemaReport, ReportStatus } from 'src/api/interactive-schema-types'
import { useGetFile, useGetSharedFileUrl } from 'src/api/file'

interface Props {
    pop: () => void
    report: InteractiveSchemaReport
}

const AttachmentFileLink = ({ fileId }: { fileId: number }) => {
    const { data: file } = useGetFile(fileId)
    const { data: fileUrl } = useGetSharedFileUrl(file?.url ?? '')
    if (!fileUrl) return null
    return (
        <Link
            href={fileUrl}
            target='_blank'
            rel='noopener noreferrer'
            underline='hover'
            variant='body2'
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
            <Description sx={{ fontSize: 16 }} />
            {file?.filename ?? `file-${fileId}`}
            <OpenInNew sx={{ fontSize: 14 }} />
        </Link>
    )
}

const ReadOnlyField = ({ label, value }: { label: string; value?: string | number | null }) => (
    <TextField
        label={label}
        value={value ?? '—'}
        size='small'
        fullWidth
        disabled
        InputLabelProps={{ shrink: true }}
    />
)

const SectionTitle = ({ children }: { children: string }) => (
    <Typography variant='subtitle1' fontWeight={600} mt={3} mb={1}>
        {children}
    </Typography>
)

const statusLabel = (status: ReportStatus, t: TFunction) => {
    switch (status) {
        case ReportStatus.Approved: return t('status-approved', { ns: 'interactive-schema' })
        case ReportStatus.Completed: return t('status-completed', { ns: 'interactive-schema' })
        case ReportStatus.InProgress: return t('status-in-progress', { ns: 'interactive-schema' })
        default: return t('status-draft', { ns: 'interactive-schema' })
    }
}

const statusColor = (status: ReportStatus): 'success' | 'primary' | 'warning' | 'default' => {
    switch (status) {
        case ReportStatus.Approved: return 'success'
        case ReportStatus.Completed: return 'primary'
        case ReportStatus.InProgress: return 'warning'
        default: return 'default'
    }
}

export const InteractiveSchemaReportViewer = ({ pop, report: initialReport }: Props) => {
    const { t } = useTranslation(['interactive-schema', 'common'])
    const [notesOpen, setNotesOpen] = useState(false)
    const { data: report, isLoading } = useGetInteractiveSchemaReportById(initialReport.id)
    const data = report ?? initialReport
    const schema = data.schema
    const rawRd = data.reportData
    const rd = typeof rawRd === 'string'
        ? (() => { try { return JSON.parse(rawRd) } catch { return {} } })()
        : rawRd ?? {}

    const parseConfig = (raw: any): { documents?: any[]; notes?: string } => {
        if (!raw) return {}
        if (typeof raw === 'string') {
            try { return JSON.parse(raw) } catch { return {} }
        }
        return raw
    }
    const config = parseConfig(schema?.configuration)
    const documents = config.documents ?? []
    const notes = config.notes

    const fieldKeyToTranslationKey = (key: string) =>
        key.replace(/([A-Z])/g, c => `-${c.toLowerCase()}`)

    const enabledSteps = [
        { key: 'general', label: t('step-general', { ns: 'interactive-schema' }), enabled: true },
        { key: 'washing', label: t('step-washing', { ns: 'interactive-schema' }), enabled: !!schema?.hasWashingSection },
        { key: 'meshBreakage', label: t('step-mesh-breakage', { ns: 'interactive-schema' }), enabled: !!schema?.hasMeshBreakageSection },
        { key: 'checklist', label: t('step-checklist', { ns: 'interactive-schema' }), enabled: !!schema?.checklistId },
        { key: 'diving', label: t('step-diving', { ns: 'interactive-schema' }), enabled: !!schema?.hasDivingCertificationSection },
        { key: 'attachments', label: t('step-attachments', { ns: 'interactive-schema' }), enabled: !!schema?.hasAttachmentsSection },
    ].filter(s => s.enabled)

    const title = schema
        ? `${t('activity-report', { ns: 'interactive-schema' })} - ${schema.name}`
        : t('activity-report', { ns: 'interactive-schema' })

    return (
        <GenericDialogLayout title={title} pop={pop} actions={[]}>
            <Box sx={{ pt: 2, pb: 4, overflowY: 'auto', maxHeight: '80vh' }}>
                {isLoading && <CircularProgress size={20} sx={{ mb: 2 }} />}

                <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 2, p: 2, mb: 3 }}>
                    <Box display='flex' flexWrap='wrap' gap={2} alignItems='center' mb={schema?.description ? 1.5 : 0}>
                        <Chip label={String(statusLabel(data.status, t))} color={statusColor(data.status)} size='small' />
                        {schema?.name && (
                            <Box>
                                <Typography variant='caption' color='text.secondary' display='block'>
                                    {t('activity', { ns: 'interactive-schema' })}
                                </Typography>
                                <Typography variant='body2' fontWeight={500}>{schema.name}</Typography>
                            </Box>
                        )}
                        {data.reportName && (
                            <Box>
                                <Typography variant='caption' color='text.secondary' display='block'>
                                    {t('reportName', { ns: 'interactive-schema' })}
                                </Typography>
                                <Typography variant='body2' fontWeight={500}>{data.reportName}</Typography>
                            </Box>
                        )}
                        {data.startDate && (
                            <Box>
                                <Typography variant='caption' color='text.secondary' display='block'>
                                    {t('startDate', { ns: 'interactive-schema' })}
                                </Typography>
                                <Typography variant='body2' fontWeight={500}>
                                    {new Date(data.startDate).toLocaleDateString('no-NO')}
                                </Typography>
                            </Box>
                        )}
                        {data.vesselName && (
                            <Box>
                                <Typography variant='caption' color='text.secondary' display='block'>
                                    {t('vesselName', { ns: 'interactive-schema' })}
                                </Typography>
                                <Typography variant='body2' fontWeight={500}>{data.vesselName}</Typography>
                            </Box>
                        )}
                    </Box>

                    {schema?.description && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant='body2' color='text.secondary'>{schema.description}</Typography>
                        </>
                    )}

                    {documents.length > 0 && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Box display='flex' flexWrap='wrap' gap={1.5} alignItems='center'>
                                {documents.map((doc, i) =>
                                    doc.url ? (
                                        <Link key={i} href={doc.url} target='_blank' rel='noopener noreferrer'
                                              underline='hover' variant='body2'
                                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {doc.title}<OpenInNew sx={{ fontSize: 14 }} />
                                        </Link>
                                    ) : null
                                )}
                            </Box>
                        </>
                    )}

                    {notes && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Box display='flex' alignItems='center' gap={0.5} sx={{ cursor: 'pointer', userSelect: 'none' }}
                                 onClick={() => setNotesOpen(p => !p)}>
                                <InfoOutlined sx={{ fontSize: 16, color: 'info.main' }} />
                                <Typography variant='body2' color='info.main' fontWeight={500}>
                                    {t('instructions', { ns: 'interactive-schema' })}
                                </Typography>
                                {notesOpen ? <ExpandLess sx={{ fontSize: 16, color: 'info.main' }} /> : <ExpandMore sx={{ fontSize: 16, color: 'info.main' }} />}
                            </Box>
                            <Collapse in={notesOpen}>
                                <Typography variant='body2' color='text.secondary' sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                                    {notes}
                                </Typography>
                            </Collapse>
                        </>
                    )}
                </Box>

                <Stepper activeStep={enabledSteps.length - 1} alternativeLabel sx={{ mb: 4 }}>
                    {enabledSteps.map(step => (
                        <Step key={step.key} completed>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <SectionTitle>{t('step-general', { ns: 'interactive-schema' })}</SectionTitle>
                <Divider sx={{ mb: 2 }} />
                <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2}>
                    <ReadOnlyField label={String(t('reportName', { ns: 'interactive-schema' }))} value={rd.general?.reportName} />
                    <ReadOnlyField
                        label={String(t('startDate', { ns: 'interactive-schema' }))}
                        value={rd.general?.startDate ? new Date(rd.general.startDate).toLocaleDateString('no-NO') : undefined}
                    />
                    <ReadOnlyField label={String(t('vesselName', { ns: 'interactive-schema' }))} value={rd.general?.vesselName} />
                </Box>

                {schema?.hasWashingSection && rd.washing && (
                    <>
                        <SectionTitle>{t('step-washing', { ns: 'interactive-schema' })}</SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2}>
                            <ReadOnlyField label={String(t('completed', { ns: 'common' }))} value={rd.washing.isCompleted ? String(t('yes', { ns: 'common' })) : String(t('no', { ns: 'common' }))} />
                            {rd.washing.completedAt && (
                                <ReadOnlyField label={String(t('completed-date', { ns: 'interactive-schema' }))} value={new Date(rd.washing.completedAt).toLocaleDateString('no-NO')} />
                            )}
                            {rd.washing.fieldValues && Object.entries(rd.washing.fieldValues).map(([key, val]) => (
                                <ReadOnlyField key={key} label={String(t(fieldKeyToTranslationKey(key), { ns: 'interactive-schema' }))} value={String(val)} />
                            ))}
                        </Box>
                    </>
                )}

                {schema?.hasMeshBreakageSection && rd.meshBreakage && (
                    <>
                        <SectionTitle>{t('step-mesh-breakage', { ns: 'interactive-schema' })}</SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2}>
                            <ReadOnlyField label={String(t('has-breakage', { ns: 'interactive-schema' }))} value={rd.meshBreakage.hasBreakage ? String(t('yes', { ns: 'common' })) : String(t('no', { ns: 'common' }))} />
                            <ReadOnlyField label={String(t('findings-count', { ns: 'interactive-schema' }))} value={rd.meshBreakage.findingsCount} />
                        </Box>
                        {data.findings && data.findings.length > 0 && (
                            <Box mt={2} display='flex' flexDirection='column' gap={1}>
                                {data.findings.map((f, i) => (
                                    <Box key={i} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, p: 1.5 }}>
                                        <Box display='grid' gridTemplateColumns='1fr 1fr 1fr 1fr' gap={1}>
                                            <ReadOnlyField label={String(t('post-number', { ns: 'interactive-schema' }))} value={f.postNumber} />
                                            <ReadOnlyField label={String(t('size', { ns: 'interactive-schema' }))} value={f.size} />
                                            <ReadOnlyField label={String(t('depth', { ns: 'interactive-schema' }))} value={f.depth} />
                                            <ReadOnlyField label={String(t('comment', { ns: 'common' }))} value={f.comment} />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}

                {schema?.checklistId && rd.checklist && schema.checklist && (
                    <>
                        <SectionTitle>{t('step-checklist', { ns: 'interactive-schema' })}</SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        <Box display='flex' flexDirection='column' gap={1.5}>
                            {schema.checklist.items?.map(item => {
                                const answer = rd.checklist?.answers?.[item.id!]
                                const displayAnswer = answer === true || answer === 'true'
                                    ? String(t('yes', { ns: 'common' }))
                                    : answer === false || answer === 'false'
                                        ? String(t('no', { ns: 'common' }))
                                        : answer ?? '—'
                                return (
                                    <Box key={item.id} display='flex' justifyContent='space-between' alignItems='center'
                                         sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, p: 1.5 }}>
                                        <Typography variant='body2'>{item.label}</Typography>
                                        <Chip label={String(displayAnswer)} size='small' variant='outlined' />
                                    </Box>
                                )
                            })}
                        </Box>
                    </>
                )}

                {schema?.hasDivingCertificationSection && rd.divingCertification && (
                    <>
                        <SectionTitle>{t('step-diving', { ns: 'interactive-schema' })}</SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        {rd.divingCertification.certifications?.map((cert: DivingCertificationEntry, i: number) => (
                            <Box key={i} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, p: 2, mb: 2 }}>
                                <Typography variant='subtitle2' mb={1}>{t('diver', { ns: 'interactive-schema' })} {i + 1}: {cert.diverName}</Typography>
                                <Box display='grid' gridTemplateColumns='1fr 1fr 1fr' gap={2}>
                                    <ReadOnlyField label={String(t('certificate-number', { ns: 'interactive-schema' }))} value={cert.certificateNumber} />
                                    <ReadOnlyField
                                        label={String(t('health-certificate-valid-until', { ns: 'interactive-schema' }))}
                                        value={cert.healthCertificateValidUntil
                                            ? new Date(cert.healthCertificateValidUntil).toLocaleDateString('no-NO')
                                            : undefined}
                                    />
                                    <ReadOnlyField label={String(t('dive-number', { ns: 'interactive-schema' }))} value={cert.diveNumber} />
                                    <ReadOnlyField label={String(t('time-in-sea', { ns: 'interactive-schema' }))} value={cert.timeInSea} />
                                    <ReadOnlyField label={String(t('leaving-surface', { ns: 'interactive-schema' }))} value={cert.timeLeavingSurface} />
                                    <ReadOnlyField label={String(t('on-bottom', { ns: 'interactive-schema' }))} value={cert.timeOnBottom} />
                                    <ReadOnlyField label={String(t('leaving-bottom', { ns: 'interactive-schema' }))} value={cert.timeLeavingBottom} />
                                    <ReadOnlyField label={String(t('on-surface', { ns: 'interactive-schema' }))} value={cert.timeOnSurface} />
                                    <ReadOnlyField label={String(t('max-depth', { ns: 'interactive-schema' }))} value={cert.maxDepth} />
                                    <ReadOnlyField label={String(t('decompression-in-sea', { ns: 'interactive-schema' }))} value={cert.decompressionInSea ? String(t('yes', { ns: 'common' })) : String(t('no', { ns: 'common' }))} />
                                    <ReadOnlyField label={String(t('repeated-dive', { ns: 'interactive-schema' }))} value={cert.repeatedDive ? String(t('yes', { ns: 'common' })) : String(t('no', { ns: 'common' }))} />
                                    {cert.nitrogenAfterDive && (
                                        <ReadOnlyField label={String(t('nitrogen-after-dive', { ns: 'interactive-schema' }))} value={cert.nitrogenAfterDive} />
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </>
                )}

                {schema?.hasAttachmentsSection && (
                    <>
                        <SectionTitle>{t('step-attachments', { ns: 'interactive-schema' })}</SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        <ReadOnlyField label={String(t('additional-comments', { ns: 'interactive-schema' }))} value={rd.attachments?.notes} />
                        {Array.isArray(rd.attachments?.files) && rd.attachments.files.length > 0 && (
                            <Box mt={1} display='flex' flexDirection='column' gap={1}>
                                {rd.attachments.files.map((attachment: any) => (
                                    <AttachmentFileLink key={attachment.fileId} fileId={attachment.fileId} />
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </GenericDialogLayout>
    )
}